import { skyMeasuredVelocity, stepSkyFireFlow } from './110-sky-fire-flow.mjs';
import { skyTerrainHeight } from './87-sky-terrain.mjs';
import { stepSkyDragonAttackRun } from './109-sky-attack-run.mjs';
import { SKY_WORLD, skyClearBossGround, skyBossWorldScale } from './86-sky-world-scale.mjs';
import {
  skyGuardianPose,
  skyGuardianWake,
  SKY_GUARDIAN_ENTRY_SECONDS,
} from './76-sky-campaign.mjs';
import { skyBossAttack } from './78-sky-combat.mjs';
import { skyGuardianAnatomy } from './83-sky-anatomy.mjs';
import { skyPrototypeGazeTarget } from './84-sky-prototype-rig.mjs';
import {
  SKY_CLOSE_ENCOUNTER,
  SKY_TRAVERSAL_TIMING,
  skyGuardianPursuit,
  skyGuardianCombatLane,
  skyGuardianThreat,
  skyGuardianAttackKind,
  skyGuardianVerticalPursuit,
  skyGuardianFacingDepth,
  skyCreateTraversal,
  skyTraversalFrame,
  skyCreateBreath,
  skyBreathContact,
  skyDragonAimControl,
  SKY_DRAGON_AIM,
} from './88-sky-encounter-beat.mjs';
/** Advance deterministic guardian combat by dt seconds. Callbacks carry the
 * existing run's scoring and damage transitions; no renderer or external clock
 * participates. A newly started traversal returns before any projectile step. */
export function stepSkyGuardian(s, dt, input, { hit, event, spawn, fireAimed }) {
  const boss = s.boss;
  if (boss.hp <= 0) return;
  const previousAge = boss.age;
  const previousMouth =
    boss.attackSocket && Math.abs(previousAge - (boss.depthAge ?? previousAge)) <= dt * 1.5
      ? { ...boss.attackSocket }
      : null;
  boss.age += dt;
  const wake = skyGuardianWake(boss.profile);
  if (
    previousAge < wake.impactAge &&
    boss.age >= wake.impactAge &&
    Math.hypot(s.x - wake.x, s.y - wake.y) < wake.radius
  ) {
    hit(s, 'wake');
    if (s.status !== 'playing') return;
  }
  Object.assign(boss, skyGuardianPose(boss.profile, boss.age, boss.artVariant, boss.design));
  const tracking = skyGuardianPursuit(
    boss.trackX,
    boss.breath?.target?.x ?? boss.aimAnchorX ?? s.x,
    boss.age,
    dt,
    boss.artVariant,
  );
  boss.trackX = tracking.anchor;
  boss.trackVX = tracking.vx;
  boss.x += tracking.x;
  boss.vx += tracking.vx;
  const lift = skyGuardianVerticalPursuit(boss.trackY, boss.breath?.target?.y ?? s.y, boss.age, dt);
  boss.trackY = lift.anchor;
  boss.trackVY = lift.vx;
  const clearance = skyClearBossGround(
    boss.y + lift.x,
    boss.vy + lift.vx,
    boss.profile,
    boss.design,
    boss.artVariant,
  );
  boss.y = clearance.y;
  boss.vy = clearance.vy;
  Object.assign(boss, skyGuardianCombatLane(boss));
  const phase = boss.hp > (boss.maxHp * 2) / 3 ? 1 : boss.hp > boss.maxHp / 3 ? 2 : 3;
  if (phase !== boss.combatPhase) {
    boss.combatPhase = phase;
    boss.phaseAge = 0;
    event(s, 'boss-phase', `GUARDIAN PHASE ${phase} · WATCH THE CHARGE`);
  }
  const continuousDepth = Math.abs(boss.age - (boss.depthAge ?? boss.age)) <= dt * 1.5;
  if (boss.artVariant === 'first-dragon') {
    const reference = {
      ...boss,
      z: continuousDepth && Number.isFinite(boss.depthZ) ? boss.depthZ : boss.z,
    };
    const anatomy = skyGuardianAnatomy(reference),
      mouth = anatomy.attackSocket;
    boss.depthMouthOffset = mouth ? mouth.z - reference.z : 0;
    boss.depthCrestOffset = anatomy.primary.z - reference.z;
  }
  const depth = skyGuardianFacingDepth(
    boss,
    continuousDepth ? boss.depthZ : undefined,
    continuousDepth ? boss.depthVZ : undefined,
    dt,
  );
  Object.assign(boss, depth, { depthZ: depth.z, depthVZ: depth.vz, depthAge: boss.age });
  const crossedEntry =
    previousAge < SKY_GUARDIAN_ENTRY_SECONDS && boss.age >= SKY_GUARDIAN_ENTRY_SECONDS;
  if (
    (crossedEntry || phase !== (boss.traversedPhase ?? phase)) &&
    boss.age >= SKY_GUARDIAN_ENTRY_SECONDS
  ) {
    boss.traversedPhase = phase;
    boss.damageStage = phase - 1;
    s.traversal = skyCreateTraversal(
      s,
      phase,
      skyBossWorldScale(boss.profile, boss.design, boss.artVariant),
      !!input.reducedMotion,
    );
    boss.traversing = true;
    boss.charge = 0;
    boss.recoil = 0;
    boss.attackPhase = 'traversal';
    boss.controlMode = 'flyby';
    boss.aimApproach = null;
    boss.aimApproached = false;
    boss.attackRun = null;
    boss.aimAnchorX = undefined;
    boss.aimWindow = false;
    boss.aimPreparing = false;
    s.aimPoint = null;
    boss.breath = null;
    s.shots = [];
    s.threats = [];
    event(s, 'guardian-traversal', 'AUTO PILOT · POSITION SIGHT · WEAPONS SAFE');
    refreshAnatomy(boss);
    return;
  }
  const before = skyBossAttack(phase, boss.phaseAge ?? 0, boss.design);
  if (stepDragonAimApproach(s, dt) && skyDragonAimControl(boss, dt))
    boss.phaseAge = (boss.phaseAge ?? 0) + dt;
  const attack = skyBossAttack(phase, boss.phaseAge, boss.design);
  if (boss.artVariant === 'first-dragon')
    boss.gazeTarget = boss.breath?.active
      ? ['x', 'y', 'z'].map(
          (key) => boss.breath.origin[key] + boss.breath.direction[key] * boss.breath.range,
        )
      : skyPrototypeGazeTarget(s, boss.age, { ...boss, ...attack });
  boss.attackKind = skyGuardianAttackKind(boss, attack.attackPattern);
  if (boss.age < SKY_GUARDIAN_ENTRY_SECONDS) {
    boss.vulnerable = false;
    boss.attackPhase = 'approach';
    boss.phaseAge = 0;
  } else {
    Object.assign(boss, attack);
    if (attack.ordinal > before.ordinal) {
      const anatomy = skyGuardianAnatomy(boss);
      const crest = anatomy.attackSocket ?? anatomy.primary;
      const origin = crest;
      boss.attackSocket = { x: origin.x, y: origin.y, z: origin.z };
      if (boss.artVariant !== 'first-dragon' || attack.ordinal % 2 === 1)
        boss.lastAttackAge = boss.age;
      if (boss.attackKind === 'flame' && boss.artVariant === 'first-dragon') {
        if (attack.ordinal % 2 === 1)
          boss.breath = skyCreateBreath(boss, s, skyMeasuredVelocity(previousMouth, origin, dt));
      } else if (attack.attackPattern === 'gate') {
        // A visible two-metre gap follows a fixed seeded lane, never the player.
        const gap = Math.sin(s.seed + attack.ordinal * 1.7) * 8;
        for (let x = -11.5; x <= 11.5; x += 1.1) {
          if (Math.abs(x - gap) < 1.3) continue;
          const bolt = spawn(s, 'threat', crest ? origin.x : x, origin.y, origin.z);
          const flight = Math.max(SKY_CLOSE_ENCOUNTER.minimumFlightSeconds, (1 - origin.z) / 24);
          bolt.vx = crest ? (x - origin.x) / flight : 0;
          bolt.vy = crest ? (boss.y - origin.y) / flight : 0;
          Object.assign(bolt, skyGuardianThreat(boss, origin, flight));
          s.threats.push(bolt);
        }
      } else fireAimed(s, origin, attack.attackPattern, boss);
    }
  }
  boss.damageStage = phase - 1;
  if (boss.breath) boss.breath.age += dt;
  if (boss.breath?.active) {
    boss.attackPhase = 'breath';
    boss.charge = boss.breath.emitting ? 1 : 0;
  }
  refreshAnatomy(boss);
  stepSkyDragonAttackRun(s, dt);
  if (boss.breath) {
    const wasEmitting = boss.breath.active;
    stepSkyFireFlow(
      boss.breath,
      dt,
      boss.attackSocket,
      skyMeasuredVelocity(previousMouth, boss.attackSocket, dt),
      boss.breath.age,
    );
    if (skyBreathContact(boss.breath, { x: s.x, y: s.y, z: SKY_WORLD.pilotZ })) hit(s, 'breath');
    if (wasEmitting && !boss.breath.active && boss.artVariant === 'first-dragon') {
      boss.controlMode = 'steer';
      boss.aimPreparing = false;
      boss.aimWindow = false;
    }
    if (!boss.breath.flowActive) boss.breath = null;
  }
}

// Solve the same articulated mouth/gaze relationship used by collision, then
// move the real pilot to that height. A camera translation cannot substitute
// for this finite protected climb from either ground or airborne launch.
function stepDragonAimApproach(s, dt) {
  const boss = s.boss;
  if (boss.artVariant !== 'first-dragon' || boss.age < SKY_GUARDIAN_ENTRY_SECONDS) return true;
  const beat = skyBossAttack(boss.combatPhase, boss.phaseAge, boss.design);
  if (beat.opening <= 0 || boss.breath?.active) {
    boss.aimApproach = null;
    boss.aimApproached = false;
    boss.aimAnchorX = undefined;
    return true;
  }
  if (boss.breath?.flowActive && !boss.breath.active) {
    // Coasting gas remains a readable, avoidable hazard. Never force the pilot
    // into a close approach/attack rail until that real material has drained.
    boss.controlMode = 'steer';
    boss.aimPreparing = false;
    boss.aimWindow = false;
    return false;
  }
  if (boss.aimApproached) return true;
  if (!beat.vulnerable && !boss.aimApproach) return true;
  boss.controlMode = 'aim-approach';
  boss.aimPreparing = true;
  boss.aimPreparation = 0;
  boss.aimWindow = false;
  s.aimPoint = null;
  if (!boss.aimApproach) {
    boss.aimAnchorX = s.x;
    boss.aimApproach = {
      age: 0,
      startX: s.x,
      targetX: s.x,
      startY: s.y,
      targetY: s.y,
      duration: null,
    };
  }
  const approach = boss.aimApproach;
  // Finish the creature's physical depth approach before solving its gaze.
  // Freezing the beat here preserves every second of the shooting window.
  if (approach.duration === null && (boss.closeHead ?? 0) < 0.95) return false;
  let x = approach.targetX,
    y = approach.targetY;
  const floor = skyTerrainHeight(s.x, SKY_WORLD.pilotZ, s.terrainTravel) + SKY_WORLD.pilotAimHeight;
  // Continue the solve against the live articulated face while climbing. A
  // frozen launch pose would leave the pilot above a head that has since bowed.
  for (let i = 0; i < (approach.duration === null ? 12 : 2); i++) {
    const pose = { ...boss, gazeTarget: skyPrototypeGazeTarget({ ...s, x, y }, boss.age, boss) };
    const anatomy = skyGuardianAnatomy(pose),
      mouth = anatomy.attackSocket;
    const faceX = (anatomy.headFocus.x + (mouth.x + anatomy.primary.x) / 2) / 2;
    x = Math.max(
      -SKY_WORLD.lateralLimit,
      Math.min(SKY_WORLD.lateralLimit, faceX + SKY_DRAGON_AIM.shoulderApproachOffset),
    );
    y = Math.max(
      floor,
      Math.min(SKY_WORLD.maximumAimHeight, mouth.y - SKY_WORLD.pilotFlightEyeHeight),
    );
  }
  if (approach.duration === null) {
    approach.startY = s.y;
    approach.startX = s.x;
    approach.duration = Math.max(1, Math.hypot(x - s.x, y - s.y) / 3);
  }
  approach.targetX = x;
  approach.targetY = y;
  approach.age = Math.min(approach.duration, approach.age + dt);
  const t = approach.age / approach.duration;
  const eased = t ** 3 * (10 - 15 * t + 6 * t * t);
  const destination = {
    y: approach.startY + (approach.targetY - approach.startY) * eased,
    x: approach.startX + (approach.targetX - approach.startX) * eased,
  };
  const distance = Math.hypot(destination.x - s.x, destination.y - s.y);
  const step = distance ? Math.min(1, (6.5 * dt) / distance) : 1;
  s.y += (destination.y - s.y) * step;
  s.x += (destination.x - s.x) * step;
  s.locomotion = 'fly';
  if (approach.age < approach.duration || distance > 6.5 * dt + 0.001) return false;
  boss.aimApproach = null;
  boss.aimApproached = true;
  boss.controlMode = 'steer';
  return true;
}

function refreshAnatomy(boss) {
  boss.anatomy = skyGuardianAnatomy(boss);
  const socket = boss.anatomy.attackSocket ?? boss.anatomy.primary;
  boss.attackSocket = { x: socket.x, y: socket.y, z: socket.z };
  boss.weakpoints = boss.anatomy.targets;
  boss.weakpoint = boss.anatomy.primary;
  boss.body = boss.anatomy.body;
  boss.vulnerable = boss.weakpoint.exposed;
  boss.aimWindow = boss.controlMode === 'aim' && !boss.aimPreparing && boss.vulnerable;
}

/** Protected scripted traversal advances only its own clock and articulation.
 * Combat age, phase clock, terrain and consumable timers remain frozen. Skip is
 * a short physical return, while pause/hidden states are stopped by caller75. */
export function stepSkyTraversal(s, input, dt, event) {
  const route = s.traversal,
    boss = s.boss;
  if (!route || !boss) return;
  s.bursts = s.bursts.filter((burst) => (burst.life -= dt) > 0);
  if ((input.skipTraversal || input.reducedMotion) && !route.returning && !route.reduced) {
    route.points = [{ ...route.position }, { ...route.origin }];
    route.returnLift = boss.y - route.center.y;
    route.returnTangent = { ...route.tangent };
    route.returnProgress = route.progress;
    route.returnCameraWeight = route.cameraWeight ?? 1;
    route.returning = true;
    route.age = 0;
    route.duration = SKY_TRAVERSAL_TIMING.returnDuration;
  }
  route.age = Math.min(route.duration, route.age + dt);
  const frame = skyTraversalFrame(route);
  const ground = skyTerrainHeight(frame.position.x, frame.position.z, s.terrainTravel);
  if (!route.reduced)
    frame.position.y = Math.max(
      frame.position.y,
      ground +
        SKY_WORLD.pilotAimHeight +
        0.4 * frame.cameraWeight * Math.min(1, frame.progress / 0.1),
    );
  const delta = Object.fromEntries(
    ['x', 'y', 'z'].map((key) => [key, frame.position[key] - route.position[key]]),
  );
  const length = Math.hypot(delta.x, delta.y, delta.z);
  if (length > 0.000001)
    frame.tangent = Object.fromEntries(
      Object.entries(delta).map(([key, value]) => [key, value / length]),
    );
  Object.assign(route, frame);
  s.x = frame.position.x;
  s.y = frame.position.y;
  s.pilotZ = frame.position.z;
  s.locomotion = route.reduced ? route.originLocomotion : 'fly';
  boss.y = route.center.y + frame.bossLift;
  boss.animationOffset = (boss.animationOffset ?? 0) + dt;
  boss.gazeTarget = [s.x, s.y, s.pilotZ];
  refreshAnatomy(boss);
  if (route.age < route.duration) return;
  Object.assign(s, {
    x: route.origin.x,
    y: route.origin.y,
    pilotZ: SKY_WORLD.pilotZ,
    traversal: null,
  });
  boss.y = route.center.y;
  boss.traversing = false;
  boss.phaseAge = 0;
  boss.controlMode = 'steer';
  skyDragonAimControl(boss, 0);
  Object.assign(boss, skyBossAttack(boss.combatPhase, boss.phaseAge, boss.design));
  boss.gazeTarget = [s.x, s.y, SKY_WORLD.pilotZ];
  s.grace = Math.max(s.grace, 1);
  refreshAnatomy(boss);
  event(
    s,
    'guardian-control',
    boss.combatPhase === 1
      ? 'FLIGHT CONTROL · SIGHT RETAINED · WEAPONS PREPARING'
      : 'FLIGHT CONTROL · WATCH THE CHARGE',
  );
}
